import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Heading,
  Stack,
} from '@chakra-ui/react'
import type { BaseParamsVehicleDto, InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { nameof } from 'ts-simple-nameof'
import * as yup from 'yup'

import { useTranslationsContext, type TranslateFunction } from 'BrokerTsLib/src/context/TranslationsContext'
import { useMemo } from 'react'
import { useSaveBaseParams } from '../hooks/useWizard'
import ErrorSummary from './ErrorSummary'
import { LeasingBasicParametersForm } from './LeasingBasicParametersForm'
import { BadgeStep } from './ui/BadgeStep'


const getBaseParamsValidationRuleObject = (translate: TranslateFunction) => {
  return {
    // POV
    [nameof<BaseParamsVehicleDto>(x => x.pov)]: yup
      .array()
      .nullable()
      .min(1, translate('step2.basicParameters.povRequiredValidation', 'Vyberte variantu povinného ručení'))

    // HAV
    , [nameof<BaseParamsVehicleDto>(x => x.hav)]: yup
      .array()
      .nullable()
      .min(1, translate('step2.basicParameters.havRequiredValidation', 'Vyberte variantu havarijního pojištění'))

    // SPOL
    , [nameof<BaseParamsVehicleDto>(x => x.spol)]: yup
      .array()
      .nullable()
      .min(1, translate('step2.basicParameters.spolRequiredValidation', 'Vyberte variantu spoluúčasti'))

    // INSURERS
    , [nameof<BaseParamsVehicleDto>(x => x.insurers)]: yup
      .array()
      .nullable()
      .min(1, translate('step2.basicParameters.insurersRequiredValidation', 'Vyberte pojišťovnu'))
  }
}

export const LeasingBasicParametersFormWrapper = ({
  insuranceParams,
  onSubmit
}: {
  insuranceParams: InsuranceParamsLeasingDto,
  onSubmit: (values: InsuranceParamsLeasingDto) => void
}) => {
  const { translate } = useTranslationsContext()
  const validationSchema = useMemo(() => {
    return getBaseParamsValidationRuleObject(translate)
  }, [translate])

  const { mutateAsync: saveParams, isPending: isLoadingSaveParams } = useSaveBaseParams(insuranceParams.generalParams.paramsId!.toString())

  const methods = useForm<InsuranceParamsLeasingDto>({
    defaultValues: insuranceParams,
    resolver: yupResolver(yup.object().shape(
      {
        baseParams: yup.object().shape(validationSchema)
      }
    ) as any),
    mode: 'onBlur'
  })

  const { handleSubmit, setError, formState: { errors, isSubmitting } } = methods

  const submitForm = handleSubmit((data) => {
    return saveParams(data)
      .then(() => onSubmit(data))
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Card>
          <CardHeader sx={{ py: 5 }}>
            <Flex sx={{ gap: 8, alignItems: 'center' }}>
              <BadgeStep>1</BadgeStep>

              <Heading sx={{ fontSize: 'lg', fontWeight: 'bold' }}>
                {translate('step2.basicParameters.title', 'Základní parametry nabídky')}
              </Heading>
            </Flex>
          </CardHeader>

          <CardBody sx={{ px: { md: 24 }, pb: { md: 8 } }}>
            {insuranceParams && <LeasingBasicParametersForm />}
          </CardBody>

          <CardFooter>
            <Stack direction='column' w='100%'>
              <Flex sx={{ w: '100%', placeContent: 'center' }}>
                <Button
                  type='button'
                  isLoading={isLoadingSaveParams}
                  disabled={isSubmitting || isLoadingSaveParams}
                  onClick={submitForm}
                >
                  {translate('step2.basicParameters.submitButton', 'Poptat pojištění')}
                </Button>
              </Flex>

              <ErrorSummary errors={errors} />
            </Stack>
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  )
}